import { lazy, Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet'

import { Navigate, Route, Routes, useLocation } from 'react-router'

import userAuthenticationState from 'logic/auth'
import { removeUniqueIdFromPath, trackPage } from 'utils/analytics'
import generatePageTitle from 'utils/generatePageTitle'

import { useSentryUser } from '../monitoring/sentry'

import Banner from './Banner'

//loading screens
import { isProductionApp } from '../const'
import LoadingAnalyticsPage from './Analytics/loading'
import APIKeysLoading from './APIKeys/loading'
import BulkMoneyFirstStepLoading from './BulkMoneySender/loading/BulkMoneyFirstStepLoading'
import FullpagetableWithHeader from './CommonLoadingScreens/fullpagetablewithheader'
import LoadingFiles from './Files/LoadingFiles'
import LoadingLoginScreen from './Login/loading'
import LoadingMessageDetails from './MessageDetails/Loading'
import ReferralsLoading from './Referrals/loading'
import SurveyRoutes from './routes/SurveyRoutes'
import WhatsappRoutes from './routes/WhatsappRoutes'
import FirststepSMSSendingLoadingScreen from './Sendsms/loading/firststep'
import LoadingSettings from './Settings/loading'
import LoadingSignUpScreen from './Signup/loadingSignup'
import LoadingDetailScreen from './Surveys/loading/LoadingDetailScreen'
import LoadingSendScreen from './Surveys/loading/LoadingSendScreen'
import TemplateReviewDecision from './Whatsapp/TemplateReviewDecision'

const Login = lazy(() => import(/* webpackChunkName: "login-page" */ './Login'))
const Signup = lazy(() => import(/* webpackChunkName: "signup-page" */ './Signup'))
const PasswordRecovery = lazy(() => import(/* webpackChunkName: "forgot-password-page" */ './ForgotPassword'))
const OtpVerification = lazy(() => import(/* webpackChunkName: "forgot-password-page" */ './OtpVerification'))
const PesaLinkTransactionApproval = lazy(
	() => import(/* webpackChunkName: "pesalink-transaction-approval" */ './PesaLinkTransactionApproval'),
)
const PhoneVerifivation = lazy(() => import(/* webpackChunkName: "phone-verification-page" */ './Phoneverification/authWrapper'))
const EmailVerificationSent = lazy(
	() => import(/* webpackChunkName: "email-verification-page" */ './Emailverification/authWrapper'),
)
const EmailConfirmed = lazy(() => import(/* webpackChunkName: "email-confirmed-page" */ './EmailConfirmed/authWrapper'))
const ResetPassword = lazy(() => import(/* webpackChunkName: "reset-password-page" */ './ResetPassword'))
const Inbox = lazy(() => import(/* webpackChunkName: "inbox-page" */ './Inbox'))
const Settings = lazy(() => import(/* webpackChunkName: "settings-page" */ './Settings'))
const AuditLogs = lazy(() => import(/* webpackChunkName: "audit-logs-page" */ './AuditLogs'))
const SendSMS = lazy(() => import(/* webpackChunkName: "sendsms-page" */ './Sendsms'))
const ApprovalDetails = lazy(() => import(/* webpackChunkName: "ApprovalDetails-page" */ './Sendsms/views/ApprovalDetails'))
const Billing = lazy(() => import(/* webpackChunkName: "billing-page" */ './Billing'))
const PaypalConfirmation = lazy(() => import(/* webpackChunkName: "paypal-confirmation-page" */ './Billing/paypalConfirmation'))
const Outbox = lazy(() => import(/* webpackChunkName: "outbox-page" */ './Outbox'))
const Contacts = lazy(() => import(/* webpackChunkName: "contacts-page" */ './Contacts'))
const ContactsDraft = lazy(() => import(/* webpackChunkName: "contacts-draft-page" */ './Contacts/views/contactDraft/index'))
const ContactsRecipients = lazy(() => import(/* webpackChunkName: "contacts-recipients" */ './Contacts/views/recipients'))
const BusinessAccount = lazy(() => import(/* webpackChunkName: "business-account-page" */ './BusinessAccount'))
const MessageDetailsPage = lazy(() => import(/* webpackChunkName: "message-details-page" */ './MessageDetails'))
const FileAttachmentPage = lazy(() => import(/* webpackChunkName: "attachments-page" */ './Files'))
const PageNotFound = lazy(() => import(/* webpackChunkName: "not-found-page" */ './PageNotFound'))
const BlockContactInfoUpdate = lazy(() => import(/* webpackChunkName: "block-contact-info-page" */ './BlockContactInfoUpdate'))
const Analytics = lazy(() => import(/* webpackChunkName: "analytics-page" */ './Analytics'))
const APIKeys = lazy(() => import(/* webpackChunkName: "api-keys-page" */ './APIKeys'))
const USSD = lazy(() => import(/* webpackChunkName: "api-keys-page" */ './USSD'))
const Onboarding = lazy(() => import(/* webpackChunkName: "onboarding-page" */ './Onboarding'))

const Surveys = lazy(() => import(/* webpackChunkName: "surveys-page" */ './Surveys'))
const SurveyOutbox = lazy(() => import(/* webpackChunkName: "survey-responses-page" */ './Surveys/Outbox'))

const BulkData = lazy(() => import(/* webpackChunkName: "Bulk-data-page" */ './BulkData'))
const SendBulk = lazy(() => import(/* webpackChunkName: "Bulk-data-send" */ './BulkData/SendBulk'))
const TransactionDetails = lazy(() => import(/* webpackChunkName: "Bulk-details-page" */ './BulkData/TransactionDetails'))

const BulkMoneySender = lazy(() => import(/*webpackChunkName: "Bulk-money-sender-page" */ './BulkMoneySender/BulkMoneySender'))
const BulkMoneySenderOutbox = lazy(
	() => import(/*webpackChunkName: "Bulk-money-sender-page outbox" */ './BulkMoneySender/outbox'),
)
const Airtime = lazy(() => import(/* webpackChunkName: "Airtime" */ './Airtime'))
const SendAirtime = lazy(() => import(/* webpackChunkName: "SendAirtime" */ './Airtime/SendAirtime'))
const AirtimeTransactionDetails = lazy(
	() => import(/* webpackChunkName: "Airtime-details-page" */ './Airtime/TransactionDetails'),
)
const AutomatedPaymentReminders = lazy(
	() => import(/* webpackChunkName: "AutomatedPaymentReminders" */ './AutomatedPaymentReminders'),
)
const Referrals = lazy(() => import(/* webpackChunkName: "referrals-page" */ './Referrals'))

export const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation()
	useEffect(() => {
		if (location.pathname !== '/contacts') {
			localStorage.removeItem('isAddFromList')
			localStorage.removeItem('groupObject')
		}
	}, [location.pathname])
	const authState = userAuthenticationState()

	if (authState.state !== 'authenticated') {
		return <Navigate to={`/login?prev=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />
	}

	return (
		<>
			<Banner />
			{children}
		</>
	)
}

const UnProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const authState = userAuthenticationState()

	if (authState.state === 'authenticated') {
		return <Navigate to='/sendsms' replace />
	}

	return <>{children}</>
}

const UniversalRoute = ({ children }: { children: React.ReactNode }) => {
	return <>{children}</>
}

function ScrollToTop() {
	const { pathname } = useLocation()
	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])
	return null
}

const LoadingScreens = () => {
	let { pathname } = useLocation()
	const screens = {
		analytics: <LoadingAnalyticsPage />,
		'api-keys': <APIKeysLoading />,
		inbox: <FullpagetableWithHeader />,
		outbox: <FullpagetableWithHeader />,
		contacts: <FullpagetableWithHeader />,
		billing: <FullpagetableWithHeader />,
		'business-account': <FullpagetableWithHeader />,
		enterprise: <FullpagetableWithHeader />,
		sendsms: <FirststepSMSSendingLoadingScreen />,
		settings: <LoadingSettings />,
		messages: <LoadingMessageDetails />,
		scheduled: <LoadingMessageDetails />,
		files: <LoadingFiles />,
		login: <LoadingLoginScreen />,
		'forgot-password': <LoadingLoginScreen />,
		'reset-password': <LoadingLoginScreen />,
		'account-verification': <LoadingLoginScreen />,
		'email-verification-sent': <LoadingLoginScreen />,
		'accounts/register/confirm/': <LoadingLoginScreen />,
		'block-contact-update/': <LoadingLoginScreen />,
		'confirm-updated-email': <LoadingLoginScreen />,
		pesalink: <LoadingLoginScreen />,
		'survey-details': <LoadingDetailScreen />,
		register: <LoadingSignUpScreen />,
		onboarding: <LoadingLoginScreen />,
		questions: <LoadingDetailScreen />,
		responses: <LoadingDetailScreen />,
		'send-survey': <LoadingSendScreen />,
		surveys: <FullpagetableWithHeader />,
		'send-bulk-money/new': <BulkMoneyFirstStepLoading />,
		'send-bulk-money': <FullpagetableWithHeader />,
		'bulk-data': <FullpagetableWithHeader />,
		airtime: <FullpagetableWithHeader />,
		referrals: <ReferralsLoading />,
		'': false,
	}
	let matchingPathName = ''
	for (const [key] of Object.entries(screens)) {
		if (pathname.includes(key)) {
			matchingPathName = key
			break
		}
	}
	return screens[matchingPathName as keyof typeof screens] || <FirststepSMSSendingLoadingScreen />
}

export default function RoutesComponent(props: any) {
	const location = useLocation()
	const page = removeUniqueIdFromPath(location)
	const pageTitle = generatePageTitle(page)
	trackPage({ page, path: location.pathname, title: pageTitle })
	const authState = userAuthenticationState()
	// @ts-ignore
	const credentials = authState.credentials || null
	useSentryUser({ credentials })

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				{isProductionApp && <script async src='https://www.googletagmanager.com/gtag/js?id=G-KZLZEV9EJ5'></script>}
			</Helmet>
			<ScrollToTop />
			<Suspense fallback={<LoadingScreens />}>
				<Routes>
					<Route path='/' element={<Navigate to='/sendsms' replace />} />
					<Route
						path='/accounts/login'
						element={
							<UnProtectedRoute>
								<Login {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/accounts/register'
						element={
							<UnProtectedRoute>
								<Signup {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/login'
						element={
							<UnProtectedRoute>
								<Login {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/register'
						element={
							<UnProtectedRoute>
								<Signup {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/forgot-password'
						element={
							<UnProtectedRoute>
								<PasswordRecovery {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/phone-verification'
						element={
							<UniversalRoute>
								<PhoneVerifivation {...props} credentials={credentials} />
							</UniversalRoute>
						}
					/>
					<Route
						path='/email-verification'
						element={
							<UniversalRoute>
								<EmailVerificationSent {...props} credentials={credentials} />
							</UniversalRoute>
						}
					/>
					<Route
						path='/otp-verification'
						element={
							<UniversalRoute>
								<OtpVerification {...props} credentials={credentials} />
							</UniversalRoute>
						}
					/>
					<Route
						path='/pesalink/imara/confirm'
						element={
							<UniversalRoute>
								<PesaLinkTransactionApproval />
							</UniversalRoute>
						}
					/>
					<Route
						path='/pesalink/imara/approve'
						element={
							<UniversalRoute>
								<PesaLinkTransactionApproval />
							</UniversalRoute>
						}
					/>
					<Route
						path='/imara-bank/transaction-verification'
						element={
							<UniversalRoute>
								<PesaLinkTransactionApproval />
							</UniversalRoute>
						}
					/>
					<Route
						path='/imara-bank/transaction-verification/:token'
						element={
							<UniversalRoute>
								<PesaLinkTransactionApproval />
							</UniversalRoute>
						}
					/>
					<Route
						path='/confirm-email/:userId/:activationKey'
						element={
							<UnProtectedRoute>
								<EmailConfirmed {...props} />
							</UnProtectedRoute>
						}
					/>
					<Route
						path='/confirm-updated-email/:userId/:activationKey'
						element={
							<PrivateRoute>
								<EmailConfirmed {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route path='/reset-password' element={<ResetPassword {...props} />} />
					<Route
						path='/block-contact-update/:userId/:uniqueKey'
						element={
							<UniversalRoute>
								<BlockContactInfoUpdate {...props} credentials={credentials} />
							</UniversalRoute>
						}
					/>
					<Route path='/inbox' element={<Navigate to='/sendsms/inbox' replace />} />
					<Route
						path='/settings'
						element={
							<PrivateRoute>
								<Settings {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/settings/:settingsView'
						element={
							<PrivateRoute>
								<Settings {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/audit-logs'
						element={
							<PrivateRoute>
								<AuditLogs {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Onboarding */}
					<Route
						path='/onboarding'
						element={
							<PrivateRoute>
								<Onboarding {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* SendSMS routes */}
					<Route path='/sendsms' element={<Navigate to='/sendsms/compose' replace />} />
					<Route
						path='/sendsms/compose'
						element={
							<PrivateRoute>
								<SendSMS {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/sendsms/inbox'
						element={
							<PrivateRoute>
								<Inbox {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/sendsms/outbox'
						element={
							<PrivateRoute>
								<Outbox {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/sendsms/outbox/:view'
						element={
							<PrivateRoute>
								<Outbox {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route path='/reports' element={<Navigate to='/sendsms/outbox/recipient-reports' replace />} />
					<Route
						path='/sendsms/:draftId/review-message'
						element={
							<PrivateRoute>
								<ApprovalDetails {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/sendsms/:currentstep/:variant'
						element={
							<PrivateRoute>
								<SendSMS {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/sendsms/:currentstep'
						element={
							<PrivateRoute>
								<SendSMS {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					<Route
						path='/automated-payment-reminders'
						element={
							<PrivateRoute>
								<AutomatedPaymentReminders {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Whatsapp routes */}
					<Route path='/whatsapp/*' element={<WhatsappRoutes {...props} credentials={credentials} />} />
					<Route path='/templates/:id/:decision' element={<TemplateReviewDecision />} />

					{/* Billing routes */}
					<Route
						path='/billing'
						element={
							<PrivateRoute>
								<Billing {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/billing/paypal-confirm/:id'
						element={
							<PrivateRoute>
								<PaypalConfirmation {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/billing/:currentView/:currentSubView'
						element={
							<PrivateRoute>
								<Billing {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/billing/:currentView'
						element={
							<PrivateRoute>
								<Billing {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Contacts routes */}
					<Route
						path='/contacts'
						element={
							<PrivateRoute>
								<Contacts {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/contacts/groups/:groupView'
						element={
							<PrivateRoute>
								<Contacts {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/contacts/draft/:contactsDraftID'
						element={
							<PrivateRoute>
								<ContactsDraft {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/contacts/:contactId/recipients'
						element={
							<PrivateRoute>
								<ContactsRecipients {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/contacts/:contactsView/:blockOption'
						element={
							<PrivateRoute>
								<Contacts {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/contacts/:contactsView'
						element={
							<PrivateRoute>
								<Contacts {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route path='/outbox' element={<Navigate to='/sendsms/outbox' replace />} />

					<Route
						path='/messages/:messageID'
						element={
							<PrivateRoute>
								<MessageDetailsPage {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/scheduled/:messageID'
						element={
							<PrivateRoute>
								<MessageDetailsPage {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Business Account routes */}
					<Route
						path='/business-account'
						element={
							<PrivateRoute>
								<BusinessAccount {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/business-account/:accountView/:accountSubView/:accountDetailsRoute'
						element={
							<PrivateRoute>
								<BusinessAccount {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/business-account/:accountView/:accountSubView'
						element={
							<PrivateRoute>
								<BusinessAccount {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/business-account/:accountView'
						element={
							<PrivateRoute>
								<BusinessAccount {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Bulk Data routes */}
					<Route
						path='/bulk-data'
						element={
							<PrivateRoute>
								<SendBulk {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/bulk-data/bulk-details/:id'
						element={
							<PrivateRoute>
								<TransactionDetails {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/bulk-data/scheduled/:id'
						element={
							<PrivateRoute>
								<TransactionDetails {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/bulk-data/:section'
						element={
							<PrivateRoute>
								<BulkData {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					<Route
						path='/files/:uuid'
						element={
							<PrivateRoute>
								<FileAttachmentPage {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/analytics'
						element={
							<PrivateRoute>
								<Analytics {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/api-keys'
						element={
							<PrivateRoute>
								<APIKeys {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/referrals'
						element={
							<PrivateRoute>
								<Referrals {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/ussd'
						element={
							<PrivateRoute>
								<USSD {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Survey routes */}
					<Route
						path='/surveys/outbox'
						element={
							<PrivateRoute>
								<SurveyOutbox {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/surveys/messages/:messageID'
						element={
							<PrivateRoute>
								<MessageDetailsPage {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route path='/surveys/:id/*' element={<SurveyRoutes {...props} credentials={credentials} />} />
					<Route path='/surveys/new/*' element={<SurveyRoutes {...props} credentials={credentials} />} />
					<Route
						path='/surveys'
						element={
							<PrivateRoute>
								<Surveys {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Airtime routes */}
					<Route
						path='/airtime/airtime-details/:id'
						element={
							<PrivateRoute>
								<AirtimeTransactionDetails {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/airtime/scheduled/:id'
						element={
							<PrivateRoute>
								<AirtimeTransactionDetails {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/airtime'
						element={
							<PrivateRoute>
								<SendAirtime {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>
					<Route
						path='/airtime/:section'
						element={
							<PrivateRoute>
								<Airtime {...props} credentials={credentials} />
							</PrivateRoute>
						}
					/>

					{/* Bulk Money Sender (dev only) */}
					{import.meta.env.DEV && (
						<>
							<Route
								path='/send-money/pending-drafts/:draftId'
								element={
									<PrivateRoute>
										<BulkMoneySender {...props} credentials={credentials} />
									</PrivateRoute>
								}
							/>
							<Route
								path='/send-money'
								element={
									<PrivateRoute>
										<BulkMoneySender {...props} credentials={credentials} />
									</PrivateRoute>
								}
							/>
							<Route
								path='/send-money/:section'
								element={
									<PrivateRoute>
										<BulkMoneySenderOutbox {...props} credentials={credentials} />
									</PrivateRoute>
								}
							/>
						</>
					)}

					<Route path='*' element={<PageNotFound />} />
				</Routes>
			</Suspense>
		</>
	)
}
